// Tabs de página (`.tabs-page` del mock): fila bajo el topbar, item activo con subrayado primary.
// Las usan las pantallas multi-sección de todos los portales (políticas, cartera, clientes…).
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface PageTab<K extends string = string> {
    key: K;
    label: string;
    /** Ícono antes del rótulo. */
    icon?: LucideIcon;
    /** Contador en chip junto al rótulo; sin él la pestaña se pinta como siempre. */
    count?: string | number;
    /** Qué cuenta el chip, para su `title`. */
    countTitle?: string;
}

/** `page` es la fila a todo lo ancho bajo el topbar; `inline` es la tira `.sk-tabs` dentro del contenido. */
export type PageTabsVariant = 'page' | 'inline';

const LIST_CLASS: Record<PageTabsVariant, string> = {
    page: 'flex flex-none border-b border-border bg-card px-5',
    inline: 'inline-flex flex-none gap-1 self-start border-b-2 border-border',
};

const TAB_CLASS: Record<PageTabsVariant, { base: string; active: string; idle: string }> = {
    page: {
        base: '-mb-px border-b-2 px-3.5 py-2 text-xs font-medium transition-colors',
        active: 'border-primary font-semibold text-foreground',
        idle: 'border-transparent text-muted-foreground hover:text-foreground',
    },
    inline: {
        base: '-mb-0.5 inline-flex items-center gap-1.5 border-b-[3px] px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors',
        active: 'border-primary text-primary',
        idle: 'border-transparent text-muted-foreground hover:text-foreground',
    },
};

/** Ids de una pestaña y de su panel, para enlazarlos con `aria-controls` / `aria-labelledby`. */
export function pageTabIds(prefix: string, key: string): { tab: string; panel: string } {
    return { tab: `${prefix}-tab-${key}`, panel: `${prefix}-panel-${key}` };
}

export function PageTabs<K extends string = string>({
    tabs,
    active,
    onChange,
    className,
    variant = 'page',
    ariaLabel,
    idPrefix,
}: {
    tabs: readonly PageTab<K>[];
    active: K;
    onChange: (key: K) => void;
    className?: string;
    variant?: PageTabsVariant;
    ariaLabel?: string;
    /** Con él cada pestaña lleva id y la activa apunta a su panel (`pageTabIds`). */
    idPrefix?: string;
}) {
    const styles = TAB_CLASS[variant];
    return (
        <div role="tablist" aria-label={ariaLabel} className={cn(LIST_CLASS[variant], className)}>
            {tabs.map((tab) => {
                const isActive = tab.key === active;
                const Icon = tab.icon;
                // La variante `inline` ya alinea en fila; la `page` solo lo hace con ícono o contador.
                const needsRow =
                    variant === 'page' && (Icon !== undefined || tab.count !== undefined);
                const ids = idPrefix === undefined ? undefined : pageTabIds(idPrefix, tab.key);
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        id={ids?.tab}
                        aria-controls={isActive ? ids?.panel : undefined}
                        aria-selected={isActive}
                        onClick={() => {
                            onChange(tab.key);
                        }}
                        className={cn(
                            styles.base,
                            needsRow && 'inline-flex items-center gap-1.5',
                            isActive ? styles.active : styles.idle,
                        )}
                    >
                        {Icon && <Icon className="size-3.5" aria-hidden />}
                        {tab.label}
                        {tab.count !== undefined && (
                            <span
                                title={tab.countTitle}
                                className={cn(
                                    'rounded-full px-1.5 py-px text-[10px] font-bold tabular-nums',
                                    isActive
                                        ? 'bg-primary/15 text-primary'
                                        : 'bg-muted text-muted-foreground',
                                )}
                            >
                                {tab.count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
